/**
 * Chartly's one way of writing a chart's numbers: 1.2M, $4.50, 12%.
 *
 * The unit comes from the chart's y_label (keep in step with Chart#value_unit):
 * a label naming dollars gets "$", one naming a percent or "%" gets "%", and
 * anything else is a plain number. Numbers of a million or more are shortened
 * (1.2M, $35.2T); ticks shorten from 10,000 (50K). The page's data table keeps
 * the exact figures. `placeLabels` keeps axis labels from overlapping.
 * A Spanish page (`locale` "es") writes them as Mexico does: 1.2 M, 4500 M,
 * 38.5 B (billones).
 */

export function unitOf(label) {
  const text = String(label ?? '')
  if (/%|\bpercent\b/i.test(text)) return 'percent'
  if (/\bdollars?\b|\bd[oó]lar(es)?\b/i.test(text)) return 'dollars'
  return null
}

const LOCALES = { es: 'es-MX' }
const cache = new Map()
function formatter(options, locale = null) {
  const tag = LOCALES[locale] ?? 'en-US'
  const key = tag + JSON.stringify(options)
  if (!cache.has(key)) cache.set(key, new Intl.NumberFormat(tag, options))
  return cache.get(key)
}

function withUnit(text, unit) {
  if (unit === 'percent') return `${text}%`
  if (unit === 'dollars') return text.startsWith('-') ? `-$${text.slice(1)}` : `$${text}`
  return text
}

/** A value for a tooltip, bar end, legend or note: 1.2M, $52,340, $4.50, 12.3%. */
export function formatNumber(value, unit = null, locale = null) {
  const abs = Math.abs(value)
  let options
  if (abs >= 1e6) options = { notation: 'compact', maximumSignificantDigits: 3 }
  else if (unit === 'dollars' && abs < 100) options = { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  else if (unit === 'percent' || abs >= 100) options = { maximumFractionDigits: 1 }
  else options = { maximumFractionDigits: 2 }
  return withUnit(formatter(options, locale).format(value), unit)
}

/**
 * An axis tick. `step` is the gap between ticks, so every tick on one axis
 * shows the same number of decimals ($2.50, $3.00, not $2.5, $3).
 */
export function formatTick(value, unit = null, step = null, locale = null) {
  const abs = Math.abs(value)
  if (abs >= 10000) return withUnit(formatter({ notation: 'compact', maximumFractionDigits: 1 }, locale).format(value), unit)
  const decimals = step ? Math.min(2, Math.max(0, -Math.floor(Math.log10(step) + 1e-9))) : 0
  const fixed = unit === 'dollars' && decimals > 0 ? 2 : decimals
  return withUnit(formatter({ minimumFractionDigits: fixed, maximumFractionDigits: fixed }, locale).format(value), unit)
}

/**
 * Keeps category labels inside the chart and apart: each is nudged in from the
 * edges, and one that would still touch the label before it is dropped, so
 * labels never overlap, even on a phone.
 */
export function placeLabels(labels, width, charWidth = 7, gap = 8) {
  const placed = []
  labels.forEach((label) => {
    const half = (label.text.length * charWidth) / 2
    const x = Math.min(Math.max(label.x, half), width - half)
    const previous = placed[placed.length - 1]
    if (previous && x - half < previous.right + gap) return
    placed.push({ ...label, x, right: x + half })
  })
  return placed
}
